import React, { useEffect, useState, useMemo } from 'react';
import {
    LineChart, Line, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid
} from 'recharts';
import { isFreeUser } from './utils/planAccess';
import LeadershipBadgesWidget from './widgets/LeadershipBadgesWidget';

const LeadershipTrends = () => {
    const [fullData, setFullData] = useState([]);
    const [data, setData] = useState([]);
    const [selectedMetric, setSelectedMetric] = useState('autonomy'); // Default metric
    const [loading, setLoading] = useState(true); // Add loading state
    const token = localStorage.getItem('token'); // Ensure token is retrieved
    const [timeRange, setTimeRange] = useState("full"); // ✅ Default: Full range
    const [activeTooltipIndex, setActiveTooltipIndex] = useState(null);
    const [difficultyFilter, setDifficultyFilter] = useState("all"); // New state for difficulty filter

    const [conversations, setConversations] = useState([]);
    const [selected, setSelected] = useState(null);
    const [conversationLoading, setConversationLoading] = useState(true);

    const ratingDescriptions = {
        1: "Very Ineffective",
        2: "Ineffective",
        3: "Somewhat Ineffective",
        4: "Neutral",
        5: "Somewhat Effective",
        6: "Effective",
        7: "Very Effective"
    };
    
    const metricDisplayNames = {
        autonomy: "Autonomy",
        relatedness: "Relatedness",
        competence: "Competence",
        feedback: "Feedback",
        listening: "Listening",
        problemSolving: "Problem Solving",
        focused: "Focused Leadership",
        inspiring: "Inspiring Leadership",
        overall: "Overall Leadership",
        managingSelf: "Managing Self",
        understandingOthers: "Understanding Others",
        managingOthers: "Managing Others"
    };
    
    const CustomDot = (props) => {
        const { cx, cy, index } = props;
        const isActive = index === activeTooltipIndex; // Check if this dot is active
    
        return (
            <circle 
                cx={cx} 
                cy={cy} 
                r={isActive ? 8 : 6}  // ✅ Active dot is larger
                fill={isActive ? "#8884d8" : "transparent"}  // ✅ Active dot is filled, normal dot is empty
                stroke={isActive ? "#555" : "#8884d8"}  // ✅ Active dot has darker border, normal dot has purple border
                strokeWidth={isActive ? 3 : 2}  // ✅ Active dot has thicker border
            />
        );
    };    
    
    const now = useMemo(() => new Date().getTime(), []);

    // ✅ Define earliestAllowedDate **once** to avoid unnecessary calculations in useEffect()
    const earliestAllowedDate = useMemo(() => {
        if (fullData.length === 0) {
            return now - (30 * 24 * 60 * 60 * 1000); // ✅ Default to last 30 days if no data
        }
    
        if (fullData.length === 1) {
            return fullData[0].date - (30 * 24 * 60 * 60 * 1000); // ✅ If one report, go 30 days before that date
        }
    
        const timeRanges = {
            week: now - (7 * 24 * 60 * 60 * 1000),
            month: now - (30.5 * 24 * 60 * 60 * 1000),
            "3months": now - (92 * 24 * 60 * 60 * 1000),
            "6months": now - (183 * 24 * 60 * 60 * 1000),
            full: data[0]?.date || now - (30 * 24 * 60 * 60 * 1000), // ✅ Default full range to first report
        };
    
        return timeRanges[timeRange] || timeRanges.full; // ✅ Return the selected range or fallback to full
    }, [fullData, timeRange, now]);    

    const calculatedTicks = useMemo(() => {
        if (data.length === 0) {
            const interval = (now - earliestAllowedDate) / 4;
            return [
                earliestAllowedDate,
                earliestAllowedDate + interval,
                earliestAllowedDate + interval * 2,
                earliestAllowedDate + interval * 3,
                now
            ];
        }
    
        const minDate = timeRange === "full" ? data[0]?.date || now : earliestAllowedDate;
        const maxDate = timeRange === "full" ? data[data.length - 1]?.date || now : now;
        const interval = (maxDate - minDate) / 4;
    
        return [
            minDate,
            minDate + interval,
            minDate + interval * 2,
            minDate + interval * 3,
            maxDate
        ];
    }, [data, timeRange, earliestAllowedDate, now]);

    const maxTickValue = useMemo(() => 
        data.length > 0 ? Math.max(...data.map(d => d.date)) : now, 
    [data, now]);

    const datasets = useMemo(() => {
        if (!fullData.length) return { full: [], week: [], month: [], "3months": [], "6months": [] };
    
        return {
            full: fullData,
            week: fullData.filter(entry => entry.date >= earliestAllowedDate),
            month: fullData.filter(entry => entry.date >= earliestAllowedDate),
            "3months": fullData.filter(entry => entry.date >= earliestAllowedDate),
            "6months": fullData.filter(entry => entry.date >= earliestAllowedDate),
        };
    }, [fullData, earliestAllowedDate]);
        
    useEffect(() => {
        const filteredData = fullData.filter(entry => difficultyFilter === "all" || entry.difficultyLevel === difficultyFilter);
        const newData = datasets[timeRange]?.filter(entry => filteredData.includes(entry)) || datasets.full.filter(entry => filteredData.includes(entry));

        if (JSON.stringify(newData) !== JSON.stringify(data)) {
            setData(newData);
        }
    }, [timeRange, datasets, difficultyFilter, fullData]);

    useEffect(() => {
        const fetchLeadershipData = async () => {
            console.log("📡 Fetching leadership analytics...");

            try {
                setLoading(true);
                const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/analytics/leadership-trends`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch data');
                }

                const responseData = await response.json();
                console.log("📊 API Response:", responseData);

                if (responseData.data && responseData.data.length > 0) {
                    // Convert Firestore timestamps & sort by date
                    const sortedData = responseData.data
                        .map(entry => ({
                            date: new Date(entry.date).getTime(),
                            scenarioTitle: entry.scenarioTitle || "Unknown Scenario",
                            difficultyLevel: entry.difficultyLevel || 0,
                            ...entry.motivation,
                            ...entry.microSkills,
                            ...entry.macroSkills,
                            ...entry.emotionalIntelligence
                        }))
                        .sort((a, b) => a.date - b.date); // ✅ Sort data by date

                    setFullData(sortedData);                                          
                } else {
                    console.log("❌ No data received from API.");
                }
            } catch (error) {
                console.error('❌ Error fetching analytics:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLeadershipData();
    }, []);

    // Fetch conversation data
    useEffect(() => {
        const fetchDialsData = async () => {
          setConversationLoading(true);
          const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/analytics/conversation-emotions`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await response.json();
          const sorted = (data.data || []).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setConversations(sorted);
          setConversationLoading(false);
        };
      
        fetchDialsData();
      }, []);
      

    const formatChartData = (dials) => {
        const length = dials.emotional_charge?.length || 0;
        return Array.from({ length }).map((_, i) => ({
            turn: i + 1,
            emotion: dials.emotional_charge?.[i] || 0,
            autonomy: dials.autonomy_motivation?.[i] || 0,
            relatedness: dials.relatedness_motivation?.[i] || 0,
            competence: dials.competence_motivation?.[i] || 0,
        }));
    };

    // Tooltip component for leadership report ratings graph
    const CustomTooltip = ({ active, payload, label }) => {
        if (!active || !payload || !payload.length) return null;
    
        const entry = payload[0].payload;
        const date = new Date(label).toLocaleDateString();
        const displayName = metricDisplayNames[selectedMetric];
        const value = entry[selectedMetric];
        const title = entry.scenarioTitle || "Untitled";
        const difficulty = 
            entry.difficultyLevel === 1 || entry.difficultyLevel === 2 ? "Beginner" :
            entry.difficultyLevel === 3 ? "Intermediate" :
            entry.difficultyLevel === 4 || entry.difficultyLevel === 5 ? "Advanced" : "Unknown";
    
        return (
            <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm">
                <div className="font-bold text-purple-700 mb-1">{title}</div>
                <div className="text-gray-600 mb-1">{date} • {difficulty}</div>
                <div><span className="font-medium">{displayName}:</span> {value}</div>
            </div>
        );
    };

    // Tooltip component for motivation/emotion ratings graph
    const MotivationTooltip = ({ active, payload, label }) => {
        if (!active || !payload || payload.length === 0) return null;
    
        const entry = payload[0].payload;
        return (
            <div className="bg-white border border-gray-300 rounded-lg shadow-lg p-3 text-sm">
                <div className="font-bold text-purple-700 mb-1">Dialogue {label}</div>
                <div className="text-gray-700">
                    <div><span className="font-medium text-red-500">Emotion:</span> {entry.emotion}</div>
                    <div><span className="font-medium text-blue-500">Autonomy:</span> {entry.autonomy}</div>
                    <div><span className="font-medium text-green-600">Relatedness:</span> {entry.relatedness}</div>
                    <div><span className="font-medium text-orange-500">Competence:</span> {entry.competence}</div>
                </div>
            </div>
        );
    };

    return (
        <div className="w-full min-w-[480px] container mx-auto p-6">
            <div className="bg-white shadow-lg rounded-lg p-6 space-y-10">
                
                <LeadershipBadgesWidget />

                <div className="bg-gray-200 shadow-lg rounded-lg p-6 mb-6 border border-gray-300">
                    <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">Leadership Trends Over Time</h2>

                    {/* Leadership Topic Buttons */}
                    <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 mb-4">
                    {Object.keys(metricDisplayNames).map((key) => {
                        const isAllowed = ["autonomy", "relatedness", "competence"].includes(key);
                        const isDisabled = isFreeUser() && !isAllowed;

                        if (isDisabled) {
                            return (
                            <a
                                key={key}
                                href="/pricing"
                                className="px-3 py-1 rounded-md shadow-sm text-xs font-medium bg-gray-200 text-gray-400 cursor-pointer hover:underline transition"
                                title="Upgrade to view this metric"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {metricDisplayNames[key]} 🔒
                            </a>
                            );
                        }
                        
                        return (
                            <button
                            key={key}
                            onClick={() => setSelectedMetric(key)}
                            className={`px-3 py-1 rounded-md shadow-sm text-xs font-medium transition duration-200 ${
                                selectedMetric === key
                                ? "bg-purple-600 text-white scale-125"
                                : "bg-white text-gray-800 border border-gray-300 hover:bg-gray-100"
                            }`}
                            >
                            {metricDisplayNames[key]}
                            </button>
                        );
                    })}
                    </div>

                    {/* Graph and Scenario List */}
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        {/* Graph */}
                        <div className="bg-white shadow-md rounded-lg p-6 h-[500px] min-w-[380px] relative">
                            {/* Duration Filters */}
                            <div className="flex justify-start items-center bg-gray-100 rounded-lg p-1 mb-4 w-fit">
                                {["full", "week", "month", "3months", "6months"].map((key) => (
                                    <button
                                        key={key}
                                        onClick={() => setTimeRange(key)}
                                        className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 ${
                                            timeRange === key
                                                ? "bg-white text-gray-800 shadow"
                                                : "bg-transparent text-gray-600 hover:bg-gray-200"
                                        }`}
                                    >
                                        {key === "full" ? "All Reports" : `Last ${key.replace("months", " Month").replace("week", " Week")}`}
                                    </button>
                                ))}
                            </div>

                            {loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                                    <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                            <ResponsiveContainer width="100%" height="90%" className="pr-4">
                                <LineChart
                                    data={data.length > 0 ? data : []}
                                    onMouseMove={(e) => setActiveTooltipIndex(e.activeTooltipIndex)}
                                    onMouseLeave={() => setActiveTooltipIndex(null)}
                                >
                                    <CartesianGrid strokeDasharray="2 2" stroke="#ddd" vertical={false} />
                                    <XAxis
                                        dataKey="date"
                                        scale="time"
                                        type="number"
                                        domain={[
                                            timeRange === "full" && data.length > 0 ? "dataMin" : earliestAllowedDate,
                                            timeRange === "full" && data.length > 0 ? maxTickValue + (2 * 24 * 60 * 60 * 1000) : now + (2 * 24 * 60 * 60 * 1000)
                                        ]}
                                        ticks={calculatedTicks}
                                        tickFormatter={(value) => new Date(value).toLocaleDateString()}
                                    />
                                    <YAxis domain={[1, 7.5]} ticks={[1, 2, 3, 4, 5, 6, 7]} />
                                    <Tooltip content={<CustomTooltip />} />
                                    <Legend />
                                    <Line
                                        type="monotone"
                                        dataKey={selectedMetric}
                                        stroke="#8884d8"
                                        dot={(props) => data.length > 0 ? <CustomDot {...props} index={props.index} /> : false}
                                        activeDot={{ r: 8, fill: "#8884d8", stroke: "#555", strokeWidth: 3 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        </div>

                        {/* Scenario List */}
                        <div className="bg-white shadow-md rounded-lg p-6 h-[500px] flex flex-col">

                            {/* Static Filter Bar */}
                            <div className="flex-shrink-0 mb-4">
                                <div className="flex justify-start items-center bg-gray-100 rounded-lg p-1 w-fit">
                                {["all", 1, 3, 5].map((level) => (
                                    <button
                                    key={level}
                                    onClick={() => setDifficultyFilter(level)}
                                    className={`px-3 py-1 rounded-lg text-sm font-medium transition duration-200 ${
                                        difficultyFilter === level
                                        ? "bg-white text-gray-800 shadow"
                                        : "bg-transparent text-gray-600 hover:bg-gray-200"
                                    }`}
                                    >
                                    {level === "all" ? "All Reports" : level === 1 ? "Beginner" : level === 3 ? "Intermediate" : "Advanced"}
                                    </button>
                                ))}
                                </div>
                            </div>

                            {/* Scrollable List with Spinner Overlay */}
                            <div className="relative flex-grow overflow-y-auto">
                                {loading && (
                                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                                    <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                                )}

                                {data.filter(entry => difficultyFilter === "all" || entry.difficultyLevel === difficultyFilter).length === 0 && !loading ? (
                                    <div className="text-center text-gray-500 py-12">
                                        <p>No reports available yet. Complete a conversation to start generating insights.</p>
                                    </div>
                                ) : (
                                    <ul className="space-y-0.5">
                                        {data
                                            .filter(entry => difficultyFilter === "all" || entry.difficultyLevel === difficultyFilter)
                                            .slice()
                                            .sort((a, b) => b.date - a.date)
                                            .map((entry, index) => (
                                                <li
                                                    key={index}
                                                    className={`p-3 rounded-lg text-sm font-medium transition duration-200 ${
                                                    index === data.length - 1 - activeTooltipIndex ? "bg-green-100 text-green-800" : "hover:bg-green-50 text-gray-700"
                                                    }`}
                                                    onMouseEnter={() => setActiveTooltipIndex(data.length - 1 - index)}
                                                    onMouseLeave={() => setActiveTooltipIndex(null)}
                                                >
                                                    <div className="flex items-center gap-2">
                                                    <span className="text-gray-900 font-bold">{entry.scenarioTitle}</span>
                                                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium shadow-sm ${
                                                        entry.difficultyLevel === 1
                                                        ? "bg-purple-200 text-purple-900"
                                                        : entry.difficultyLevel === 3
                                                        ? "bg-purple-200 text-purple-900"
                                                        : entry.difficultyLevel === 5
                                                        ? "bg-purple-200 text-purple-900"
                                                        : "bg-gray-200 text-gray-600"
                                                    }`}>
                                                        {entry.difficultyLevel === 1
                                                        ? "Beginner"
                                                        : entry.difficultyLevel === 3
                                                        ? "Intermediate"
                                                        : entry.difficultyLevel === 5
                                                        ? "Advanced"
                                                        : "Unknown"}
                                                    </span>
                                                    <span className="text-xs text-gray-500">{new Date(entry.date).toLocaleDateString()}</span>
                                                    <span className="px-2 py-0.5 bg-white text-gray-800 border border-gray-300 rounded-full text-xs shadow-sm text-center whitespace-nowrap">
                                                        {ratingDescriptions[entry[selectedMetric]] === "Neither Effective nor Ineffective"
                                                        ? "Neutral"
                                                        : ratingDescriptions[entry[selectedMetric]] || "Unknown"}
                                                    </span>
                                                    </div>
                                                </li>
                                            ))
                                        }
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ---------------------------- */}
                {/* Conversation Dials Over Time */}
                {/* ---------------------------- */}
                <div className="bg-gray-200 shadow-lg rounded-lg p-6 mt-10 border border-gray-300">
                    <h2 className="text-2xl font-bold text-gray-800 text-center mb-6">Motivation & Emotion Timeline</h2>
                    <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                        <div className="bg-white shadow-md rounded-lg p-4 h-[500px] overflow-y-auto relative">
                        
                        {conversationLoading && (
                            <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                            <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                            </div>
                        )}

                            <ul className="space-y-2">
                                {conversations.map((conv, index) => {
                                    const isSelected = selected?.conversationId === conv.conversationId;
                                    const normalizedLevel = conv.difficultyLevel === 2 ? 1 : conv.difficultyLevel === 4 ? 5 : conv.difficultyLevel;

                                    return (
                                        <li key={index}>
                                            <div
                                                className={`cursor-pointer p-2 rounded transition duration-150 ${
                                                    isSelected
                                                        ? "bg-purple-100 text-purple-900 font-semibold"
                                                        : "hover:bg-gray-100 text-gray-800"
                                                }`}
                                                onClick={() => setSelected(isSelected ? null : conv)}
                                            >
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <strong>{conv.scenarioTitle}</strong>
                                                    <span
                                                        className={`px-2 py-0.5 rounded-full text-xs font-medium shadow-sm ${
                                                            normalizedLevel === 1 || normalizedLevel === 3 || normalizedLevel === 5
                                                                ? "bg-white text-gray-800 border border-gray-300"
                                                                : "bg-gray-200 text-gray-600"
                                                        }`}
                                                    >
                                                        {normalizedLevel === 1
                                                            ? "Beginner"
                                                            : normalizedLevel === 3
                                                            ? "Intermediate"
                                                            : normalizedLevel === 5
                                                            ? "Advanced"
                                                            : "Unknown"}
                                                    </span>
                                                    <span className="text-xs text-gray-500">
                                                        {new Date(conv.createdAt).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            </div>

                                            {isSelected && (
                                                <div className="mt-2 ml-2 p-3 bg-white border border-gray-200 rounded-lg text-sm max-h-48 overflow-y-auto space-y-2">
                                                    {conv.history && conv.history.length > 0 ? (
                                                    (() => {
                                                        let userCounter = 1;
                                                        return conv.history.map((line, idx) => {
                                                        const isUser = line.role === 'user';
                                                        return (
                                                            <div key={idx} className="whitespace-pre-wrap">
                                                            <strong className={`text-gray-800 ${isUser ? 'text-blue-600' : 'text-green-600'}`}>
                                                                {isUser ? `${userCounter++}. You` : 'Employee'}:
                                                            </strong>{" "}
                                                            {line.content}
                                                            </div>
                                                        );
                                                        });
                                                    })()
                                                    ) : (
                                                    <p className="text-sm text-gray-500">No dialogue recorded.</p>
                                                    )}
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                        <div className="bg-white shadow-md rounded-lg p-6 h-[500px] relative">
                            {conversationLoading ? (
                                <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75 z-10">
                                    <div className="w-10 h-10 border-4 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            ) : isFreeUser() ? (
                                <div className="flex flex-col items-center justify-center h-full text-center px-4">
                                    <p className="text-gray-700 text-lg font-semibold mb-2">Motivation & Emotion Insights</p>
                                    <p className="text-gray-500 mb-4">
                                        Upgrade your subscription to unlock detailed emotional engagement analytics for your conversations.
                                    </p>
                                    <a
                                        href="/pricing"
                                        className="inline-block mt-2 px-4 py-2 text-sm font-semibold bg-yellow-100 text-yellow-800 rounded hover:bg-yellow-200 transition"
                                        >
                                        See Plans →
                                    </a>
                                </div>
                            ) : selected ? (
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={formatChartData(selected.dials)}>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="turn" />
                                        <YAxis domain={[0, 100]} />
                                        <Tooltip content={<MotivationTooltip />} />
                                        <Legend />
                                        <Line type="monotone" dataKey="emotion" stroke="#e57373" />
                                        <Line type="monotone" dataKey="autonomy" stroke="#64b5f6" />
                                        <Line type="monotone" dataKey="relatedness" stroke="#81c784" />
                                        <Line type="monotone" dataKey="competence" stroke="#ffb74d" />
                                    </LineChart>
                                </ResponsiveContainer>
                            ) : (
                                <p className="text-gray-600 text-center">Select a conversation to see the chart.</p>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LeadershipTrends;
